import {
  CanvasTexture,
  DataTexture,
  DoubleSide,
  LinearFilter,
  Mesh,
  NearestFilter,
  PlaneGeometry,
  RedFormat,
  ShaderMaterial,
  Color,
  UnsignedByteType,
  Vector4,
  type Scene,
} from 'three';
import { City, CellKind } from '@/world/city';
import { PALETTE } from '@/config';

/** How many ink-ripples can be alive on the floor at once. */
const RIPPLE_MAX = 8;

/**
 * The ground is a single plane whose surface is drawn entirely in a shader that
 * samples a grid-sized data texture of cell kinds. Streets, plazas and the raw
 * "paper" of the un-built frontier are therefore rendered for free and update the
 * instant the city model changes — no per-cell meshes, no draw-call growth as the
 * city expands. This is the counterpart to the InstancedMesh skyline: one plane,
 * one draw call, the entire floor of the world.
 */
export class Ground {
  readonly mesh: Mesh;
  readonly material: ShaderMaterial;
  private tex: DataTexture;
  private data: Uint8Array<ArrayBuffer>;
  private readonly grid: number;
  private readonly extent: number;

  /* The walked-path ink: an offscreen canvas the FinaleDirector paints the
   * player's whole recorded route into, revealed across the floor during the
   * ceremony and left faint forever after. Sized to the ground plane's world
   * extent so canvas pixels map 1:1 to world XZ. */
  private readonly pathSize = 512;
  private pathCanvas: HTMLCanvasElement;
  private pathCtx: CanvasRenderingContext2D | null;
  private pathTex: CanvasTexture;

  /** Ring buffer of live ripples: (x, z, birthTime, strength). strength 0 = dead. */
  private ripples: Vector4[] = [];
  private rippleCursor = 0;

  constructor(scene: Scene, city: City) {
    this.grid = city.grid;
    // Back the texture with an explicit ArrayBuffer so the typed array is
    // Uint8Array<ArrayBuffer>, which three's DataTexture (BufferSource) requires.
    this.data = new Uint8Array(new ArrayBuffer(this.grid * this.grid));
    this.tex = new DataTexture(this.data, this.grid, this.grid, RedFormat, UnsignedByteType);
    this.tex.minFilter = NearestFilter;
    this.tex.magFilter = NearestFilter;
    this.tex.needsUpdate = true;

    const extent = city.grid * city.cellSize;
    this.extent = extent;
    // The mesh reaches well past the logical city so that from the finale's
    // aerial vantage the paper runs to the horizon instead of ending in a hard
    // edge. Everything beyond `extent` samples as Void (paper) and the radial
    // fade has long since taken over; the path texture clamps to a blank border.
    const geo = new PlaneGeometry(extent * 2.6, extent * 2.6, 1, 1);
    geo.rotateX(-Math.PI / 2);

    // Path-ink canvas & texture (blank until paintPath is called). flipY=false so a
    // world point maps to the same texel whether we draw it or sample it.
    this.pathCanvas = document.createElement('canvas');
    this.pathCanvas.width = this.pathSize;
    this.pathCanvas.height = this.pathSize;
    this.pathCtx = this.pathCanvas.getContext('2d');
    this.pathTex = new CanvasTexture(this.pathCanvas);
    this.pathTex.flipY = false;
    this.pathTex.minFilter = LinearFilter;
    this.pathTex.magFilter = LinearFilter;
    this.pathTex.needsUpdate = true;

    for (let i = 0; i < RIPPLE_MAX; i++) this.ripples.push(new Vector4(0, 0, 0, 0));

    this.material = new ShaderMaterial({
      side: DoubleSide,
      uniforms: {
        uCity: { value: this.tex },
        uGrid: { value: this.grid },
        uCell: { value: city.cellSize },
        uExtent: { value: extent },
        uGround: { value: new Color(PALETTE.ground) },
        uStreet: { value: new Color(PALETTE.road) },
        uGridLine: { value: new Color(PALETTE.groundGrid) },
        uPaper: { value: new Color(PALETTE.fog) },
        uInk: { value: new Color(PALETTE.accent) },
        uVitality: { value: city.vitality },
        uTime: { value: 0 },
        uRipples: { value: this.ripples },
        uPath: { value: this.pathTex },
        uPathReveal: { value: 0 },
      },
      vertexShader: /* glsl */ `
        varying vec2 vWorld;
        void main() {
          vec4 wp = modelMatrix * vec4(position, 1.0);
          vWorld = wp.xz;
          gl_Position = projectionMatrix * viewMatrix * wp;
        }
      `,
      fragmentShader: /* glsl */ `
        precision highp float;
        varying vec2 vWorld;
        uniform sampler2D uCity;
        uniform float uGrid, uCell, uExtent, uVitality, uTime;
        uniform vec3 uGround, uStreet, uGridLine, uPaper, uInk;
        uniform vec4 uRipples[${RIPPLE_MAX}]; // (x, z, birthTime, strength)
        uniform sampler2D uPath;
        uniform float uPathReveal;

        float readKind(vec2 cell) {
          vec2 uv = (cell + 0.5) / uGrid;
          if (uv.x < 0.0 || uv.x > 1.0 || uv.y < 0.0 || uv.y > 1.0) return 0.0;
          return floor(texture2D(uCity, uv).r * 255.0 + 0.5);
        }

        void main() {
          // World -> cell space. Grid centre is (uGrid-1)/2.
          vec2 c = vWorld / uCell + (uGrid - 1.0) * 0.5;
          vec2 cell = floor(c + 0.5);
          vec2 frac = c - cell; // [-0.5, 0.5] within the cell

          float kind = readKind(cell);

          // Base surface by cell kind.
          vec3 col = uGround;
          if (kind < 0.5) {
            // Void frontier — unresolved paper, softly noisy.
            col = uPaper;
          } else if (abs(kind - 2.0) < 0.5) {
            col = uStreet;
          } else if (abs(kind - 3.0) < 0.5) {
            col = mix(uGround, uPaper, 0.4); // plaza, a touch lighter
          }

          // Cell-boundary grid lines (the architect's drawing).
          float lineW = 0.02;
          vec2 edge = abs(frac);
          float g = smoothstep(0.5 - lineW, 0.5, max(edge.x, edge.y));
          col = mix(col, uGridLine, g * 0.5 * step(0.5, kind));

          // Street centre-lines.
          if (abs(kind - 2.0) < 0.5) {
            float centre = 1.0 - smoothstep(0.0, 0.04, min(edge.x, edge.y));
            col = mix(col, uInk, centre * 0.18);
          }

          // Vitality drains colour and value from the whole floor when the player
          // stagnates — the city literally greys out.
          float lum = dot(col, vec3(0.299, 0.587, 0.114));
          col = mix(vec3(lum), col, mix(0.35, 1.0, uVitality));
          col *= mix(0.82, 1.0, uVitality);

          // Walked-path ink — the drawn record of everywhere the player has been.
          // Applied AFTER the vitality greying so the ink always reads, even when a
          // stagnant city has washed the floor toward grey.
          vec2 pathUV = (vWorld / uExtent) + 0.5;
          float pathInk = texture2D(uPath, pathUV).a * uPathReveal * 0.85;
          col = mix(col, uInk, clamp(pathInk, 0.0, 0.85));

          // Ink-ripples: a quiet expanding ring at every place the city changed.
          // Each ripple's radius grows ~4 m/s (capped ~70m), a soft ~3m ring that
          // darkens the paper a little and fades over ~18s. Deliberately subtle —
          // "something changed here", never a splash.
          float ink = 0.0;
          for (int i = 0; i < ${RIPPLE_MAX}; i++) {
            vec4 rp = uRipples[i];
            if (rp.w <= 0.0) continue;
            float age = uTime - rp.z;
            if (age < 0.0 || age > 18.0) continue;
            float radius = min(age * 4.0, 70.0);
            float d = distance(vWorld, rp.xy);
            // Gaussian band centred on the expanding radius (~3m wide).
            float band = exp(-pow((d - radius) / 3.0, 2.0));
            float fade = 1.0 - age / 18.0;      // linear life fade
            float rise = smoothstep(0.0, 0.6, age); // ease in over the first frames
            ink += band * fade * rise * rp.w;
          }
          col = mix(col, uInk, min(0.22, ink));

          // Radial fade into fog at the outer frontier hides the plane's edge.
          float r = length(vWorld) / (uExtent * 0.5);
          col = mix(col, uPaper, smoothstep(0.72, 1.0, r));

          gl_FragColor = vec4(col, 1.0);
        }
      `,
    });

    this.mesh = new Mesh(geo, this.material);
    this.mesh.receiveShadow = true;
    this.mesh.position.y = 0;
    this.mesh.renderOrder = -1;
    scene.add(this.mesh);

    this.syncFromCity(city);
  }

  /** Copy the city's cell kinds into the data texture. Call when layout changes. */
  syncFromCity(city: City): void {
    for (let cz = 0; cz < this.grid; cz++) {
      for (let cx = 0; cx < this.grid; cx++) {
        this.data[cz * this.grid + cx] = city.kindAt(cx, cz) as CellKind;
      }
    }
    this.tex.needsUpdate = true;
  }

  /**
   * Drop an expanding ink-ring on the floor at world (x, z). Called by the
   * evolution engine at every change's epicenter, and by the finale (with a large
   * strength) at the city centre. Overwrites the oldest slot when all are live.
   */
  ripple(x: number, z: number, strength = 1): void {
    const now = this.material.uniforms.uTime.value as number;
    const slot = this.ripples[this.rippleCursor];
    slot.set(x, z, now, strength);
    this.rippleCursor = (this.rippleCursor + 1) % RIPPLE_MAX;
  }

  /**
   * Draw the player's whole walked route (flat [x0,z0,x1,z1,…] world coords) into
   * the path canvas as a soft ink stroke, then upload it. Two passes — a wide,
   * faint pass for an ink-bleed halo and a thin, darker pass for the line itself —
   * give the mark a brushed, hand-inked quality. Cheap enough to redraw wholesale
   * (called once at the ceremony and once on an epilogue boot).
   */
  paintPath(points: number[]): void {
    const ctx = this.pathCtx;
    if (!ctx) return;
    const size = this.pathSize;
    ctx.clearRect(0, 0, size, size);

    if (points.length >= 4) {
      const toPx = (x: number, z: number): [number, number] => [
        (x / this.extent + 0.5) * size,
        (z / this.extent + 0.5) * size,
      ];
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';

      const stroke = (color: string, width: number): void => {
        ctx.strokeStyle = color;
        ctx.lineWidth = width;
        ctx.beginPath();
        const [x0, y0] = toPx(points[0], points[1]);
        ctx.moveTo(x0, y0);
        for (let i = 2; i < points.length; i += 2) {
          const [px, py] = toPx(points[i], points[i + 1]);
          ctx.lineTo(px, py);
        }
        ctx.stroke();
      };

      // Soft ink bleed first, then the crisper line on top.
      stroke('rgba(30, 26, 22, 0.16)', 6);
      stroke('rgba(24, 20, 16, 0.5)', 2);
    }

    this.pathTex.needsUpdate = true;
  }

  /** How strongly the walked path shows through, in [0,1]. */
  setPathReveal(v: number): void {
    this.material.uniforms.uPathReveal.value = v;
  }

  update(city: City, elapsed: number): void {
    this.material.uniforms.uVitality.value = city.vitality;
    this.material.uniforms.uTime.value = elapsed;
  }

  dispose(): void {
    this.tex.dispose();
    this.pathTex.dispose();
    this.mesh.geometry.dispose();
    this.material.dispose();
    this.mesh.removeFromParent();
  }
}
